/**
 * ⚠️ CRITICAL STANDARDS COMPLIANCE ⚠️
 *
 * This component MUST use Material Design 3 web components from @material/web.
 *
 * ✅ ALLOWED:
 * - <md-filled-button>, <md-outlined-button>, <md-text-button>
 * - <md-outlined-text-field>, <md-filled-text-field>
 * - <md-filled-select>, <md-outlined-select> with <md-select-option>
 *
 * ❌ FORBIDDEN:
 * - Native <button>, <input>, <select>, <textarea>
 *
 * See: /guides/STANDARDS_STYLES.md#material-design-3
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
let DfFirestoreForm = class DfFirestoreForm extends LitElement {
    static { this.styles = css `
    :host {
      display: block;
      padding: var(--df-spacing-5, 1.5rem);
      background: var(--md-sys-color-surface, #fff);
      border-radius: 16px;
      border: 1px solid var(--md-sys-color-outline-variant, rgba(15, 23, 42, 0.1));
      box-shadow: 0 15px 45px rgba(15, 23, 42, 0.12);
      max-width: 520px;
    }

    h2 {
      margin: 0 0 1.5rem;
      font-size: 1.4rem;
      color: var(--md-sys-color-on-surface, #0f172a);
    }

    form {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    md-outlined-text-field {
      width: 100%;
    }

    md-filled-select {
      width: 100%;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      margin-top: 0.5rem;
    }

    .error {
      padding: 0.75rem 1rem;
      border-radius: 12px;
      background: var(--md-sys-color-error-container, rgba(220, 38, 38, 0.14));
      color: var(--md-sys-color-on-error-container, #b91c1c);
      font-size: 0.9rem;
      margin-bottom: 1rem;
    }
  `; }
    constructor() {
        super();
        this._title = '';
        this._description = '';
        this._priority = 'medium';
        this._tagsInput = '';
        this._dueDateValue = '';
        this._error = null;
        this.mode = 'create';
        this.todo = null;
    }
    updated(changed) {
        if (changed.has('todo')) {
            this.syncFromTodo();
        }
        if (changed.has('mode') && this.mode === 'create' && changed.get('mode') !== 'create') {
            this.reset();
        }
    }
    firstUpdated() {
        this.syncFromTodo();
    }
    render() {
        return html `
      <h2>${this.mode === 'create' ? 'Create todo' : 'Edit todo'}</h2>
      ${this._error ? html `<div class="error">${this._error}</div>` : nothing}
      <form @submit=${this.handleSubmit}>
        <md-outlined-text-field
          label="Title"
          required
          name="title"
          .value=${this._title}
          @input=${(event) => (this._title = event.target.value)}
          supporting-text="Describe the task succinctly">
        </md-outlined-text-field>

        <md-outlined-text-field
          label="Description"
          type="textarea"
          rows="4"
          required
          name="description"
          .value=${this._description}
          @input=${(event) => (this._description = event.target.value)}
          supporting-text="Explain the purpose or teaching prompt">
        </md-outlined-text-field>

        <md-filled-select
          label="Priority"
          name="priority"
          .value=${this._priority}
          @change=${(event) => (this._priority = event.target.value)}>
          <md-select-option value="low">
            <div slot="headline">Low</div>
          </md-select-option>
          <md-select-option value="medium" selected>
            <div slot="headline">Medium</div>
          </md-select-option>
          <md-select-option value="high">
            <div slot="headline">High</div>
          </md-select-option>
        </md-filled-select>

        <md-outlined-text-field
          label="Tags"
          name="tags"
          .value=${this._tagsInput}
          @input=${(event) => (this._tagsInput = event.target.value)}
          supporting-text="Comma separated, e.g. teaching, realtime">
        </md-outlined-text-field>

        <md-outlined-text-field
          label="Due date"
          type="date"
          name="due"
          .value=${this._dueDateValue}
          @input=${(event) => (this._dueDateValue = event.target.value)}
          supporting-text="Optional">
        </md-outlined-text-field>

        <div class="actions">
          <md-outlined-button type="button" @click=${this.handleCancel}>
            Cancel
          </md-outlined-button>
          <md-filled-button type="submit">
            ${this.mode === 'create' ? 'Create todo' : 'Save changes'}
          </md-filled-button>
        </div>
      </form>
    `;
    }
    handleSubmit(event) {
        event.preventDefault();
        this._error = null;
        if (!this._title.trim()) {
            this._error = 'Title is required.';
            return;
        }
        if (!this._description.trim()) {
            this._error = 'Description is required.';
            return;
        }
        const tags = this._tagsInput
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);
        const detail = {
            mode: this.mode,
            todoId: this.todo?.id,
            draft: {
                title: this._title.trim(),
                description: this._description.trim(),
                priority: this._priority,
                tags,
                dueDate: this._dueDateValue ? new Date(this._dueDateValue) : null,
            },
        };
        this.dispatchEvent(new CustomEvent('df-firestore-form-submit', {
            bubbles: true,
            composed: true,
            detail,
        }));
        if (this.mode === 'create') {
            this.reset();
        }
    }
    handleCancel() {
        this.dispatchEvent(new CustomEvent('df-firestore-form-cancel', {
            bubbles: true,
            composed: true,
        }));
        if (this.mode === 'create') {
            this.reset();
        }
    }
    reset() {
        this._title = '';
        this._description = '';
        this._priority = 'medium';
        this._tagsInput = '';
        this._dueDateValue = '';
        this._error = null;
    }
    syncFromTodo() {
        if (!this.todo || this.mode !== 'edit') {
            return;
        }
        this._title = this.todo.title;
        this._description = this.todo.description;
        this._priority = this.todo.priority;
        this._tagsInput = this.todo.tags.join(', ');
        this._dueDateValue = this.todo.dueDate ? this.formatDateInput(this.todo.dueDate) : '';
    }
    formatDateInput(date) {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
};
__decorate([
    property({ type: String })
], DfFirestoreForm.prototype, "mode", void 0);
__decorate([
    property({ attribute: false })
], DfFirestoreForm.prototype, "todo", void 0);
__decorate([
    state()
], DfFirestoreForm.prototype, "_title", void 0);
__decorate([
    state()
], DfFirestoreForm.prototype, "_description", void 0);
__decorate([
    state()
], DfFirestoreForm.prototype, "_priority", void 0);
__decorate([
    state()
], DfFirestoreForm.prototype, "_tagsInput", void 0);
__decorate([
    state()
], DfFirestoreForm.prototype, "_dueDateValue", void 0);
__decorate([
    state()
], DfFirestoreForm.prototype, "_error", void 0);
DfFirestoreForm = __decorate([
    customElement('df-firestore-form')
], DfFirestoreForm);
export { DfFirestoreForm };
//# sourceMappingURL=df-firestore-form.js.map